import { SolicitudInventarioRepository } from '../repositories/SolicitudInventarioRepository';
import { ReservaInventarioRepository } from '../repositories/ReservaInventarioRepository';
import { pb } from '../helpers/pocketbase_helper';
import { formatCommentWithTimestamp } from '../utils/dateHelper';

/**
 * InventarioAllocationService
 * Handles the complex logic for allocating inventory between reservations and requests
 * Implements the allocation algorithm specified in the technical spec
 */

const solicitudRepo = new SolicitudInventarioRepository();
const reservaRepo = new ReservaInventarioRepository();

export const InventarioAllocationService = {
  /**
   * Main allocation algorithm
   * When a material is requested for a production order:
   * 1. If fully available -> create reservation
   * 2. If partially available -> create reservation + request
   * 3. If not available -> create request only
   * @param {string} ordenId - Production order ID
   * @param {string} materialId - Material ID
   * @param {number} cantidadNecesaria - Required quantity
   * @param {boolean} inventarioLocal - Whether to use local inventory
   * @param {boolean} isAutomatic - Whether this is an automatic creation (from order creation)
   * @param {number} ordenNumero - Order number (for automatic messages)
   * @param {string} comentarioManual - Manual comment from user (optional)
   */
  async requestMaterial(ordenId, materialId, cantidadNecesaria, inventarioLocal = true, isAutomatic = false, ordenNumero = null, comentarioManual = null) {
    try {
      // Get current user
      const usuarioId = pb.authStore.model?.id;
      if (!usuarioId) {
        throw new Error("Usuario no autenticado");
      }

      // Get producto_inventario for this material
      const inventario = await this.getProductoInventarioForMaterial(materialId);
      
      if (!inventario) {
        throw new Error("Material no encontrado en inventario");
      }

      // Calculate available quantity
      const disponible = inventario.total - inventario.reservado;

      let resultado = {
        reservado: 0,
        solicitado: 0,
        reservaId: null,
        solicitudId: null
      };

      // Scenario 1: All needed is available
      if (disponible >= cantidadNecesaria) {
        // Check if order is active
        let isOrdenActiva = false;
        try {
          const orden = await pb.collection("orden_produccion").getOne(ordenId);
          isOrdenActiva = !!orden.activa;
        } catch (err) {
          console.warn("Could not check if order is active", { ordenId, error: err });
        }

        // Check if reservation already exists for this material
        const existingReserva = await this.findReservation(ordenId, inventario.id);
        
        // Generate automatic comment if needed
        let comentario = comentarioManual || null;
        if (isAutomatic && !comentarioManual) {
          if (ordenNumero) {
            comentario = formatCommentWithTimestamp(`Material reservado automáticamente para orden #${ordenNumero}`);
          } else {
            comentario = formatCommentWithTimestamp('Material reservado automáticamente');
          }
        }
        
        // Always create a new reservation to maintain history
        // Don't update existing reservations, create new ones for each request
        const createData = {
          orden_produccion: ordenId,
          producto_inventario: inventario.id,
          total: cantidadNecesaria,
          usuario: usuarioId,
          completado: isOrdenActiva, // Mark as completed if order is active
          activo: true,
          inventario_local: inventarioLocal
        };
        if (comentario) {
          createData.comentario = comentario;
        }
        const reserva = await reservaRepo.create(createData);
        
        // Only add reservation ID to producto_inventario reserva array if order is NOT active
        // If order is active, reservation is completed immediately and shouldn't be in the array
        if (!isOrdenActiva) {
          const currentReservas = inventario.reserva || [];
          if (!currentReservas.includes(reserva.id)) {
            await pb.collection("producto_inventario").update(inventario.id, {
              reserva: [...currentReservas, reserva.id]
            });
          }
        }

        // Update producto_inventario - get fresh data to avoid race conditions
        const currentInventario = await pb.collection("producto_inventario").getOne(inventario.id);
        const updateData = {};
        
        // If order is active, mark reservation as completed and deduct from total immediately
        // Don't increase reservado because the material is used immediately
        if (isOrdenActiva) {
          // Mark reservation as completed
          await reservaRepo.update(reserva.id, { completado: true });
          
          // Remove reservation from array (since it's completed)
          const currentReservas = currentInventario.reserva || [];
          const nuevoReservasArray = currentReservas.filter(id => id !== reserva.id);
          
          // Deduct from total, don't increase reservado
          updateData.total = Math.max(0, (currentInventario.total || 0) - cantidadNecesaria);
          updateData.reserva = nuevoReservasArray;
          updateData.reservado = currentInventario.reservado || 0; // Keep reservado unchanged
          
          console.log(`✅ Reserva completa: ${cantidadNecesaria} unidades (orden activa - completada y descontada del total)`);
        } else {
          // Order not active: normal reservation flow
          updateData.reservado = (currentInventario.reservado || 0) + cantidadNecesaria;
        }
        
        await pb.collection("producto_inventario").update(inventario.id, updateData);

        resultado.reservado = cantidadNecesaria;
        resultado.reservaId = reserva.id;

        if (!isOrdenActiva) {
          console.log(`✅ Reserva completa: ${cantidadNecesaria} unidades reservadas`);
        }
      }
      // Scenario 2: Partial availability
      else if (disponible > 0) {
        const cantidadSolicitada = cantidadNecesaria - disponible;

        // Check if order is active
        let isOrdenActiva = false;
        try {
          const orden = await pb.collection("orden_produccion").getOne(ordenId);
          isOrdenActiva = !!orden.activa;
        } catch (err) {
          console.warn("Could not check if order is active", { ordenId, error: err });
        }

        // Generate automatic comment for reservation if needed
        let comentarioReserva = comentarioManual || null;
        if (isAutomatic && !comentarioManual) {
          comentarioReserva = formatCommentWithTimestamp(`Reserva parcial automática - cantidad reservada: ${disponible}`);
        }
        
        // Always create a new reservation to maintain history
        // Don't update existing reservations, create new ones for each request
        const createReservaData = {
          orden_produccion: ordenId,
          producto_inventario: inventario.id,
          total: disponible,
          usuario: usuarioId,
          completado: isOrdenActiva, // Mark as completed if order is active
          activo: true,
          inventario_local: inventarioLocal
        };
        if (comentarioReserva) {
          createReservaData.comentario = comentarioReserva;
        }
        const reserva = await reservaRepo.create(createReservaData);
        
        // Only add reservation ID to producto_inventario reserva array if order is NOT active
        // If order is active, reservation is completed immediately and shouldn't be in the array
        if (!isOrdenActiva) {
          const currentReservas = inventario.reserva || [];
          if (!currentReservas.includes(reserva.id)) {
            await pb.collection("producto_inventario").update(inventario.id, {
              reserva: [...currentReservas, reserva.id]
            });
          }
        }

        // Generate automatic comment for request if needed
        let comentarioSolicitud = comentarioManual || null;
        if (isAutomatic && !comentarioManual) {
          comentarioSolicitud = formatCommentWithTimestamp(`Solicitud creada automáticamente - cantidad solicitada: ${cantidadSolicitada}`);
        }
        
        // Create request for missing quantity
        const createSolicitudData = {
          orden_produccion: ordenId,
          producto_inventario: inventario.id,
          total: cantidadSolicitada,
          usuario: usuarioId,
          completado: false,
          activo: true
        };
        if (comentarioSolicitud) {
          createSolicitudData.comentario = comentarioSolicitud;
        }
        const solicitud = await solicitudRepo.create(createSolicitudData);
        
        // Add solicitud ID to producto_inventario solicitud array
        const currentSolicitudes = inventario.solicitud || [];
        if (!currentSolicitudes.includes(solicitud.id)) {
          await pb.collection("producto_inventario").update(inventario.id, {
            solicitud: [...currentSolicitudes, solicitud.id]
          });
        }

        // Update producto_inventario - get fresh data to avoid race conditions
        const currentInventario = await pb.collection("producto_inventario").getOne(inventario.id);
        const updateData = {
          solicitado: (currentInventario.solicitado || 0) + cantidadSolicitada
        };
        
        // If order is active, mark reservation as completed and deduct from total immediately
        // Don't increase reservado because the material is used immediately
        if (isOrdenActiva) {
          // Mark reservation as completed
          await reservaRepo.update(reserva.id, { completado: true });
          
          // Remove reservation from array (since it's completed)
          const currentReservas = currentInventario.reserva || [];
          const nuevoReservasArray = currentReservas.filter(id => id !== reserva.id);
          
          // Deduct from total, don't increase reservado
          updateData.total = Math.max(0, (currentInventario.total || 0) - disponible);
          updateData.reserva = nuevoReservasArray;
          updateData.reservado = currentInventario.reservado || 0; // Keep reservado unchanged
          
          console.log(`⚠️ Reserva parcial: ${disponible} completado y descontado del total (orden activa), ${cantidadSolicitada} solicitado`);
        } else {
          // Order not active: normal reservation flow
          updateData.reservado = (currentInventario.reservado || 0) + disponible;
        }
        
        await pb.collection("producto_inventario").update(inventario.id, updateData);

        resultado.reservado = disponible;
        resultado.solicitado = cantidadSolicitada;
        resultado.reservaId = reserva.id;
        resultado.solicitudId = solicitud.id;

        if (!isOrdenActiva) {
          console.log(`⚠️ Reserva parcial: ${disponible} reservado, ${cantidadSolicitada} solicitado`);
        }
      }
      // Scenario 3: Nothing available
      else {
        // Generate automatic comment for request if needed
        let comentarioSolicitud = comentarioManual || null;
        if (isAutomatic && !comentarioManual) {
          comentarioSolicitud = formatCommentWithTimestamp(`Solicitud creada automáticamente - cantidad solicitada: ${cantidadNecesaria}`);
        }
        
        const createSolicitudData = {
          orden_produccion: ordenId,
          producto_inventario: inventario.id,
          total: cantidadNecesaria,
          usuario: usuarioId,
          completado: false,
          activo: true
        };
        if (comentarioSolicitud) {
          createSolicitudData.comentario = comentarioSolicitud;
        }
        const solicitud = await solicitudRepo.create(createSolicitudData);
        
        // Add solicitud ID to producto_inventario solicitud array
        const currentSolicitudes = inventario.solicitud || [];
        if (!currentSolicitudes.includes(solicitud.id)) {
          await pb.collection("producto_inventario").update(inventario.id, {
            solicitud: [...currentSolicitudes, solicitud.id]
          });
        }

        // Update producto_inventario - get fresh data to avoid race conditions
        const currentInventario = await pb.collection("producto_inventario").getOne(inventario.id);
        await pb.collection("producto_inventario").update(inventario.id, {
          solicitado: (currentInventario.solicitado || 0) + cantidadNecesaria
        });

        resultado.solicitado = cantidadNecesaria;
        resultado.solicitudId = solicitud.id;

        console.log(`❌ Sin disponibilidad: ${cantidadNecesaria} solicitado`);
      }

      // Mark order as having pending materials if there's a request
      if (resultado.solicitado > 0) {
        await pb.collection("orden_produccion").update(ordenId, {
          materiales_pendientes: true
        });
      }

      return resultado;
    } catch (err) {
      console.error("Error en requestMaterial:", err);
      throw err;
    }
  },

  /**
   * Accept a pending request
   * When accepting a request:
   * 1. Validate quantities
   * 2. Check inventory availability
   * 3. Update inventory (decrease total, increase reservado, decrease solicitado)
   * 4. Create or update reservation
   * 5. Mark request as completado
   * 6. If all requests complete, update order
   */
  async acceptRequest(solicitudId, cantidadAceptada) {
    try {
      // Get solicitud with relations
      const solicitud = await solicitudRepo.getById(solicitudId, {
        expand: 'orden_produccion,producto_inventario'
      });

      if (!solicitud) {
        throw new Error("Solicitud no encontrada");
      }

      // Validate quantity
      if (cantidadAceptada <= 0) {
        throw new Error("La cantidad debe ser mayor a 0");
      }

      if (cantidadAceptada > solicitud.total) {
        throw new Error("No se puede aceptar más de lo solicitado");
      }

      // Get inventario
      const inventario = await pb.collection("producto_inventario").getOne(solicitud.producto_inventario);

      if (!inventario) {
        throw new Error("Inventario del material no encontrado");
      }

      // Check if order is active
      let isOrdenActiva = false;
      try {
        const orden = await pb.collection("orden_produccion").getOne(solicitud.orden_produccion);
        isOrdenActiva = !!orden.activa;
      } catch (err) {
        console.warn("Could not check if order is active", { ordenId: solicitud.orden_produccion, error: err });
      }

      // When accepting a request:
      // - If order is NOT active: material arrives, total increases, reservado increases
      // - If order is active: material is already present (or arrives), we create reservation and deduct immediately
      
      const nuevoSolicitado = Math.max(0, inventario.solicitado - solicitud.total); // Decrease solicitado
      
      // Find or create reservation (always, to maintain data consistency)
      let reservation = await this.findReservation(
        solicitud.orden_produccion,
        inventario.id
      );

      if (reservation) {
        // Update existing reservation - preserve and append comment from request
        const existingComment = reservation.comentario || '';
        const solicitudComment = solicitud.comentario || '';
        let nuevoComentario = existingComment;
        
        if (solicitudComment) {
          if (existingComment) {
            // Append request comment to existing reservation comment
            nuevoComentario = `${existingComment}\n${formatCommentWithTimestamp(`Solicitud aceptada: ${solicitudComment}`)}`;
          } else {
            // No existing comment, use request comment with timestamp
            nuevoComentario = formatCommentWithTimestamp(`Solicitud aceptada: ${solicitudComment}`);
          }
        }
        
        reservation = await reservaRepo.update(reservation.id, {
          total: (reservation.total || 0) + cantidadAceptada,
          comentario: nuevoComentario
        });
      } else {
        // Create new reservation with inventario_local=true
        // This indicates the material is available locally (from accepted request)
        const usuarioId = pb.authStore.model?.id;
        const solicitudComment = solicitud.comentario || '';
        const comentarioReserva = solicitudComment 
          ? formatCommentWithTimestamp(`Solicitud aceptada: ${solicitudComment}`)
          : formatCommentWithTimestamp('Solicitud aceptada');
        
        reservation = await reservaRepo.create({
          orden_produccion: solicitud.orden_produccion,
          producto_inventario: inventario.id,
          total: cantidadAceptada,
          usuario: usuarioId,
          completado: false,
          activo: true,
          inventario_local: true, // Local inventory flag
          comentario: comentarioReserva
        });
      }

      // Add reservation ID to producto_inventario reserva array if not already there
      const currentReservas = inventario.reserva || [];
      if (!currentReservas.includes(reservation.id)) {
        await pb.collection("producto_inventario").update(inventario.id, {
          reserva: [...currentReservas, reservation.id]
        });
      }

      let nuevoReservado = inventario.reservado || 0;
      let finalTotal = inventario.total || 0;
      let nuevoReservasArray = inventario.reserva || [];

      if (isOrdenActiva) {
        // Order is active: mark reservation as completed and deduct from total immediately
        // The reservation exists (so it appears in the order), but it's completed and the total is deducted
        // Material is assumed to be already present (or just arrived), so we deduct directly from current total
        await reservaRepo.update(reservation.id, {
          completado: true
        });

        // Remove reservation ID from array (since it's completed)
        nuevoReservasArray = (inventario.reserva || []).filter(id => id !== reservation.id);
        
        // Deduct from current total immediately (material was used right away)
        // Don't add to total first - material is already present or just arrived
        finalTotal = Math.max(0, (inventario.total || 0) - cantidadAceptada);
        // Don't increase reservado (material was used immediately, not reserved)
        nuevoReservado = inventario.reservado || 0;

        console.log(`[InventarioAllocationService] Orden activa - reserva ${reservation.id} creada, completada y descontada del total inmediatamente (${inventario.total} -> ${finalTotal})`);
      } else {
        // Order is NOT active: material was already added manually to inventory
        // When accepting request, we only convert solicitud to reserva
        // Do NOT increase total - it was already added manually before accepting
        finalTotal = inventario.total || 0; // Keep total unchanged
        nuevoReservado = (inventario.reservado || 0) + cantidadAceptada; // Increase reservado (convert solicitud to reserva)
        // Keep reservation in array
        nuevoReservasArray = inventario.reserva || [];
      }

      // Update inventory with all values at once
      await pb.collection("producto_inventario").update(inventario.id, {
        total: finalTotal,
        reservado: nuevoReservado,
        solicitado: nuevoSolicitado,
        reserva: nuevoReservasArray
      });

      // Mark request as completado
      console.log(`[InventarioAllocationService] Marcando solicitud ${solicitudId} como completada`);
      const updatedSolicitud = await solicitudRepo.update(solicitudId, {
        completado: true
      });
      console.log(`[InventarioAllocationService] Solicitud actualizada:`, {
        id: updatedSolicitud.id,
        completado: updatedSolicitud.completado,
        activo: updatedSolicitud.activo
      });

      // Check if all requests for order are completado
      const allRequestsComplete = await this.checkAllRequestsComplete(
        solicitud.orden_produccion
      );

      console.log(`[InventarioAllocationService] Todas las solicitudes completadas: ${allRequestsComplete}`);

      if (allRequestsComplete) {
        await pb.collection("orden_produccion").update(solicitud.orden_produccion, {
          materiales_pendientes: false
        });
        console.log(`[InventarioAllocationService] Orden ${solicitud.orden_produccion} marcada como materiales_pendientes=false`);
      } else {
        console.log(`[InventarioAllocationService] Aún hay solicitudes pendientes para la orden ${solicitud.orden_produccion}`);
      }

      console.log(`✅ Solicitud aceptada: ${cantidadAceptada} unidades`);

      return {
        success: true,
        message: `Solicitud aceptada: ${cantidadAceptada} unidades`
      };
    } catch (err) {
      console.error("Error en acceptRequest:", err);
      throw err;
    }
  },

  /**
   * Helper: Get producto_inventario for a material/tinta
   */
  async getProductoInventarioForMaterial(materialId) {
    try {
      // First, try to get the material and check if it has producto_inventario assigned
      let material;
      try {
        material = await pb.collection("material").getOne(materialId, {
          expand: 'producto_inventario'
        });
        
        if (material && material.producto_inventario) {
          // Get the producto_inventario record
          const productoInventario = await pb.collection("producto_inventario").getOne(material.producto_inventario);
          if (productoInventario && productoInventario.activo !== false) {
            return productoInventario;
          }
        }
      } catch (materialErr) {
        // Material not found, try as tinta
        console.log("Material not found, trying as tinta:", materialErr.message);
      }

      // Try searching in tinta
      try {
        const tinta = await pb.collection("tinta").getOne(materialId);
        const tintaResult = await pb.collection("producto_inventario").getList(1, 1000, {
          filter: `tinta = "${materialId}" && activo = true`,
        });

        if (tintaResult.items.length > 0) {
          return tintaResult.items[0];
        }
      } catch (tintaErr) {
        console.log("Tinta not found:", tintaErr.message);
      }

      // If no producto_inventario found, throw descriptive error
      const materialName = material?.nombre || materialId;
      throw new Error(`El material "${materialName}" no tiene producto_inventario asignado. Por favor, asigne un producto_inventario al material antes de crear la solicitud.`);
    } catch (err) {
      console.error("Error getting producto_inventario:", err);
      // If error already has a descriptive message, throw it as is
      if (err.message && err.message.includes('producto_inventario')) {
        throw err;
      }
      // Otherwise, wrap it
      throw new Error(`Error al obtener producto_inventario para el material: ${err.message || String(err)}`);
    }
  },

  /**
   * Helper: Find existing reservation for order and material
   */
  async findReservation(ordenId, productoInventarioId) {
    try {
      const result = await pb.collection("reserva_inventario").getList(1, 1000, {
        filter: `orden_produccion = "${ordenId}" && producto_inventario = "${productoInventarioId}"`,
      });

      return result.items.length > 0 ? result.items[0] : null;
    } catch (err) {
      console.error("Error finding reservation:", err);
      return null;
    }
  },

  /**
   * Helper: Check if all requests for an order are completado
   */
  async checkAllRequestsComplete(ordenId) {
    try {
      const allRequests = await pb.collection("solicitud_inventario").getList(1, 1000, {
        filter: `orden_produccion = "${ordenId}" && activo = true`,
      });

      if (allRequests.items.length === 0) {
        return true; // No requests = all complete
      }

      return allRequests.items.every(req => req.completado === true);
    } catch (err) {
      console.error("Error checking request completion:", err);
      return false;
    }
  },

  /**
   * Helper: Check if all materials are available for an order
   */
  async checkMaterialsAvailable(ordenId) {
    try {
      console.log(`[InventarioAllocationService] Verificando materiales para orden ${ordenId}`);
      
      // Check if there are pending requests
      const pendingRequests = await pb.collection("solicitud_inventario").getList(1, 1000, {
        filter: `orden_produccion = "${ordenId}" && completado = false && activo = true`,
      });

      console.log(`[InventarioAllocationService] Solicitudes pendientes: ${pendingRequests.items.length}`);
      
      // Log details of pending requests for debugging
      if (pendingRequests.items.length > 0) {
        console.log(`[InventarioAllocationService] Detalles de solicitudes pendientes:`, 
          pendingRequests.items.map(req => ({
            id: req.id,
            total: req.total,
            completado: req.completado,
            activo: req.activo,
            producto_inventario: req.producto_inventario
          }))
        );
        return false; // There are pending requests
      }

      // Check if all reservations have enough material available
      const reservations = await pb.collection("reserva_inventario").getList(1, 1000, {
        filter: `orden_produccion = "${ordenId}" && completado = false && activo = true`,
        expand: "producto_inventario"
      });

      console.log(`[InventarioAllocationService] Reservas encontradas: ${reservations.items.length}`);

      for (const reservation of reservations.items) {
        const inventario = reservation.expand?.producto_inventario;
        if (!inventario) {
          console.log(`[InventarioAllocationService] Inventario no encontrado para reserva ${reservation.id}`);
          continue;
        }

        const total = inventario.total || 0;
        const reservado = inventario.reservado || 0;
        const solicitado = inventario.solicitado || 0;
        const cantidadReservada = reservation.total || 0;
        const inventarioLocal = reservation.inventario_local;

        console.log(`[InventarioAllocationService] Material ${inventario.id}: Total=${total}, Reservado=${reservado}, Solicitado=${solicitado}, CantidadReservada=${cantidadReservada}, InventarioLocal=${inventarioLocal}`);

        // For local reservations (inventario_local=true), check if total is sufficient
        if (inventarioLocal === true) {
          // If there's a reservation, we need to check if the total is sufficient to cover it
          // The reservation already exists, so we just need to verify the total is enough
          console.log(`[InventarioAllocationService] Reserva local - Total=${total}, CantidadReservada=${cantidadReservada}`);
          
          if (total < cantidadReservada) {
            console.log(`[InventarioAllocationService] Material local insuficiente: Total=${total} < CantidadReservada=${cantidadReservada}`);
            return false; // Not enough total material to cover the reservation
          }
        } else {
          // For external reservations (inventario_local=false), consider them as available
          // External supplier will provide the material, so no local inventory check needed
          console.log(`[InventarioAllocationService] Reserva externa - proveedor suministrará el material`);
        }
      }

      console.log(`[InventarioAllocationService] Todos los materiales están disponibles`);
      return true;
    } catch (err) {
      console.error("[InventarioAllocationService] Error checking material availability:", err);
      return false;
    }
  }
};

export default InventarioAllocationService;
